import {
  Box,
  Card,
  Checkbox,
  defaultTheme,
  Link,
  NewCarousel as Carousel,
} from "@bca/ui-library";
import React, { useState, useEffect, useCallback } from "react";
import styled from "styled-components";
import { border, BorderProps } from "styled-system";

import PopoverDesktop from "../print/PopoverDesktop";
import { VehicleResultCardProps } from "./VehicleResultCard.type";
import getCMSDomain from "./getCMSDomain";
import getBackgroundColor from "./getBackgroundColor";
import getEnvironment from "../getEnvironment";
import { IVehicleDetailsModule } from "../common/analytics/analytics.types";
import analytics from "../common/analytics/analytics";
import { IProductPushable } from "../common/analytics/analytics.types";
import ScrollTo from "../utils/ScrollTo";
import extractSelectedImages from "../utils/extractSelectedImages";
import VehicleLabels from "./components/VehicleLabels";
import LinkToDetailsPage, {
  itemCleanedLink,
} from "./components/LinkToDetailsPage";
import VehicleNotes from "./components/VehicleNotes";
import pushCarouselAnalytics from "../analytics/pushCarouselAnalytics";

const ShadowedCard = styled(Card)<BorderProps>`
  ${border}

  box-shadow: ${defaultTheme.shadows.default};
  :hover {
    box-shadow: ${defaultTheme.shadows.hover};
  }
  margin-left: 0;
  overflow: visible;
`;

const GreyBox = styled(Box)`
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
`;

const StyledLink = styled(Link)`
  font-size: 20px;
  margin: 16px 0;
  font-weight: 500;
`;

const StyledBox = styled(Box)`
  height: auto;
  padding-left: 20px;
  padding-top: 20px;
  padding-bottom: 20px;
  z-index: 0;
  position: relative;
`;

export const StyledImage = styled.img<{ marginTop: string }>`
  border-radius: 8px;
  object-fit: cover;
  margin-top: ${(props) => props.marginTop};
`;

function removeWhiteSpace(vrm: string) {
  return vrm.replace(/\s+/g, "");
}

const pushSelectAnalytics = (
  vehicleAnalytics: IVehicleDetailsModule,
  action: "select" | "unselect"
) => {
  analytics.push<IProductPushable>({
    event: {
      name: "click",
      category: "button",
      label: "vehicle-select",
      action,
    },
    product: {
      vehicle: [vehicleAnalytics],
    },
  });
};

const VehicleResultCardDesktop: React.FunctionComponent<
  VehicleResultCardProps
> = ({
  images,
  exportId,
  vrmNumber,
  lotNumber,
  primaryDescription,
  keyDecisionInfoSlot,
  recentlyAddedBadge,
  saleInfoSlot,
  secondaryInfoSlot,
  trackBtnSlot,
  pricingInfoSlot,
  printUrls,
  userNote,
  isTracked,
  vehicleAnalytics,
  selectedVehicles,
  setSelectedVehicles,
  showFilters,
  viewportWidth,
  pdfEnabled,
  imageCarouselIndexes,
}) => {
  const [isCarouselAnalyticsEnabled, setIsCarouselAnalyticsEnabled] =
    useState<boolean>(false);
  const [backgroundColor, setBackgroundColor] = useState("");
  const CMSdomainURL = getCMSDomain(getEnvironment());

  const getSelectedStatus = useCallback(() => {
    return Boolean(selectedVehicles?.find((vrm) => vrm === exportId));
  }, [selectedVehicles, exportId]);

  const addToSelection = () => {
    setSelectedVehicles([...selectedVehicles, exportId]);
  };

  const removeFromSelection = () => {
    setSelectedVehicles((selectedVehicles) => {
      return selectedVehicles.filter((vrm) => vrm !== exportId);
    });
  };

  useEffect(() => {
    setBackgroundColor(getBackgroundColor(getSelectedStatus(), isTracked));
  }, [isTracked, getSelectedStatus]);

  let queryParamCardItem: string = "";
  let deepLinkScrollFinished: boolean = false;

  if (!deepLinkScrollFinished) {
    const urlParams = new URLSearchParams(window.location.search);
    queryParamCardItem = urlParams?.get("vrm");
  }

  const handleScrollEnd = () => {
    deepLinkScrollFinished = true;
  };

  const getIsQueryVrmMatch = (vrm: string) => {
    return vrm === queryParamCardItem;
  };

  const getIsDeepLinking = (vrm: string) => {
    return !deepLinkScrollFinished && getIsQueryVrmMatch(vrm);
  };

  const isExpandedCard = !showFilters && viewportWidth === "desktop";

  return (
    <ScrollTo
      id={removeWhiteSpace(vrmNumber)}
      behavior="smooth"
      onScrollEnd={handleScrollEnd}
      active={getIsDeepLinking(removeWhiteSpace(vrmNumber))}
      key={vrmNumber}
    >
      <ShadowedCard
        width="100%"
        height="100%"
        paddingX="0"
        paddingY="0"
        marginY={0}
        border={
          getSelectedStatus() &&
          `1px solid ${defaultTheme.colors["msg-blue-border"]}`
        }
        css={
          getSelectedStatus() &&
          `
          :hover {
            box-shadow: 0px 3px 25px ${defaultTheme.colors["msg-blue-border"]};
          }
        `
        }
      >
        <LinkToDetailsPage
          CMSdomainURL={CMSdomainURL}
          vrmNumber={vrmNumber}
          dataTestId="card-link-desktop"
        />
        <Box width="auto" height="100%" display="flex" flexDirection="row">
          <Box display="flex" flexDirection="column" width="calc(100% - 175px)">
            <Box display="flex" flexDirection="row">
              <StyledBox>
                {(showFilters || viewportWidth === "smallDesktop") && (
                  <Box marginBottom={4}>
                    <VehicleLabels
                      recentlyAddedBadge={recentlyAddedBadge}
                      vrmNumber={vrmNumber}
                      lotNumber={lotNumber}
                      dataTestId="lotNumberDesktop"
                      viewportWidth={viewportWidth}
                    />
                  </Box>
                )}
                <LinkToDetailsPage
                  CMSdomainURL={CMSdomainURL}
                  vrmNumber={vrmNumber}
                  dataTestId="card-link-desktop"
                />
                {images.length === 1 ? (
                  <StyledImage
                    src={images[0]?.url}
                    alt={images[0]?.alt}
                    width={isExpandedCard ? "483px" : "328px"}
                    height={isExpandedCard ? "272px" : "219px"}
                    marginTop={"0px"}
                  />
                ) : (
                  <Carousel
                    onLoad={() => {
                      setIsCarouselAnalyticsEnabled(true);
                    }}
                    onPageChange={(page) => {
                      pushCarouselAnalytics(
                        vehicleAnalytics,
                        page,
                        isCarouselAnalyticsEnabled
                      );
                    }}
                    showArrows="showOnHover"
                    width={isExpandedCard ? "483px" : "328px"}
                    height={isExpandedCard ? "272px" : "219px"}
                    borderRadius="xs"
                    paginationVariant="dots"
                    paginationDisplay="inside"
                  >
                    {extractSelectedImages({
                      images,
                      indexes: imageCarouselIndexes,
                    }).map((image, index) => (
                      <StyledImage
                        src={image?.url}
                        alt={image?.alt}
                        key={index}
                        width={isExpandedCard ? "509px" : "341px"}
                        height={isExpandedCard ? "272px" : "219px"}
                        marginTop="0px"
                      />
                    ))}
                  </Carousel>
                )}
              </StyledBox>
              <Box
                height="auto"
                width="auto"
                padding="20px"
                display="flex"
                flexDirection="column"
              >
                {isExpandedCard && (
                  <Box width="auto" height="auto" mb="20px">
                    <VehicleLabels
                      recentlyAddedBadge={recentlyAddedBadge}
                      vrmNumber={vrmNumber}
                      lotNumber={lotNumber}
                      dataTestId="lotNumberDesktop"
                      viewportWidth={viewportWidth}
                    />
                  </Box>
                )}
                <Box width="auto" height="auto" paddingY="0px">
                  {saleInfoSlot}
                </Box>
                <StyledLink href={itemCleanedLink(CMSdomainURL, vrmNumber)}>
                  {primaryDescription}
                </StyledLink>
                <Box width="auto" height="auto">
                  {secondaryInfoSlot}
                </Box>
                <Box width="auto" height="auto" mt={4}>
                  {keyDecisionInfoSlot}
                </Box>
              </Box>
            </Box>
            {Boolean(userNote) && <VehicleNotes userNote={userNote} />}
          </Box>
          <GreyBox
            width="175px"
            height="auto"
            backgroundColor={backgroundColor}
            display="flex"
            flexDirection="column"
          >
            <Box
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              m={3}
            >
              <Box
                display="flex"
                alignItems="center"
                css={`
                  z-index: 1;
                `}
              >
                {trackBtnSlot}
                {printUrls && printUrls?.length > 0 && (
                  <PopoverDesktop
                    printUrls={printUrls}
                    vehicleAnalytics={vehicleAnalytics}
                    pdfEnabled={pdfEnabled}
                  />
                )}
              </Box>
              <Box
                p={0}
                onClick={(event) => {
                  event.stopPropagation();
                }}
                alignSelf="center"
              >
                <Checkbox
                  id="result-card-checkbox"
                  checked={getSelectedStatus()}
                  onChange={() => {
                    pushSelectAnalytics(
                      vehicleAnalytics,
                      getSelectedStatus() ? "unselect" : "select"
                    );
                    if (getSelectedStatus() === true) {
                      removeFromSelection();
                      return;
                    }
                    addToSelection();
                  }}
                  css={`
                    z-index: 0;
                  `}
                ></Checkbox>
              </Box>
            </Box>
            <Box m={5} mt="auto" height="100%">
              {pricingInfoSlot}
            </Box>
          </GreyBox>
        </Box>
      </ShadowedCard>
    </ScrollTo>
  );
};

export default VehicleResultCardDesktop;
